/**
 * `/mix` 板総合カタログの板ラインナップ正本。
 *
 * 既知板の短い表示名・絵文字・成人情報を定義し、実在確認は
 * bootstrap の `GET /boards` 結果との交差で行う。bootstrap 層(features
 * import 禁止)と Worker の両方から参照するため `src/config/` に置く。
 * 設定カード用のアイコン画像と説明文はこのファイルに置かず、UI層
 * (`features/settings/components/sections/boardCards.ts`) で slug へ対応付ける。
 * Worker bundleへ画像importを持ち込まないためと、設定画面でしか使わない
 * 文言を初期グラフのbootstrap chunkへ常駐させないため。
 */

export type MixSharePercent = 5 | 10 | 20 | 30;

/**
 * 総合カタログでの取り分。数値は1ページに混ぜる割合(%)の上限、
 * uncappedは上限なしで全体の並びへそのまま混ざる。
 */
export type MixShare = MixSharePercent | "uncapped";

export type MixLineupEntry = Readonly<{
  slug: string;
  emoji: string;
  /** 板バッジ・スレ立てチップ用の短い板名。 */
  label: string;
  /** R-18指定の板か。 */
  isAdult: boolean;
}>;

export const MIX_PUBLIC_BASE_PATH = "/mix";

/** URL、API入力、保存値で共通の板slug形式。 */
export function isValidBoardSlug(slug: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/.test(slug);
}

/** 初期表示は管理画面の公開設定を参照する。 */
export function isDefaultMixBoard(slug: string): boolean {
  return getBoardPresentations()[slug]?.defaultVisible === true;
}

export const MIX_LINEUP: readonly MixLineupEntry[] = Object.freeze([
  Object.freeze({
    slug: "aimg",
    emoji: "🟡",
    label: "二次裏",
    isAdult: true,
  }),
  Object.freeze({
    slug: "ai2",
    emoji: "❤️",
    label: "AI裏",
    isAdult: true,
  }),
  Object.freeze({
    slug: "gore",
    emoji: "⚠",
    label: "ぐろ",
    isAdult: true,
  }),
  Object.freeze({
    slug: "dark",
    emoji: "🟪",
    label: "ダーク",
    isAdult: true,
  }),
  Object.freeze({
    slug: "long",
    emoji: "☕",
    label: "まったり",
    isAdult: false,
  }),
  Object.freeze({
    slug: "news",
    emoji: "📰",
    label: "ニュース速報板",
    isAdult: false,
  }),
]);

/**
 * ローカル開発backendにはラインナップslugが存在しないため、devビルドに
 * 限り汎用テスト板を総合カタログ対象の候補へ加える(本番bundleには効かない)。
 */
const DEV_FALLBACK_LINEUP: readonly MixLineupEntry[] = Object.freeze([
  Object.freeze({
    slug: "board1",
    emoji: "🧪",
    label: "board1",
    isAdult: false,
  }),
  Object.freeze({
    slug: "board2",
    emoji: "🧪",
    label: "board2",
    isAdult: false,
  }),
]);

function defaultIncludeDevFallback(): boolean {
  return Boolean(import.meta.env.DEV);
}

function effectiveMixLineup(
  includeDevFallback: boolean,
): readonly MixLineupEntry[] {
  return includeDevFallback
    ? [...MIX_LINEUP, ...DEV_FALLBACK_LINEUP]
    : MIX_LINEUP;
}

/** `/mix` とその配下パスだけを総合カタログのマウントとして受理する。 */
export function isMixMountPath(pathname: string): boolean {
  return (
    pathname === MIX_PUBLIC_BASE_PATH ||
    pathname.startsWith(`${MIX_PUBLIC_BASE_PATH}/`)
  );
}

/** ラインナップ(dev環境ではフォールバック込み)のslug一覧。 */
export function mixLineupSlugs(
  includeDevFallback: boolean = defaultIncludeDevFallback(),
): string[] {
  return effectiveMixLineup(includeDevFallback).map((entry) => entry.slug);
}

/** 登録済みslug群をラインナップ定義順で絞り込む(未知slugは総合カタログ対象外)。 */
export function filterMixLineupSlugs(
  registered: readonly string[],
  includeDevFallback: boolean = defaultIncludeDevFallback(),
): string[] {
  const registeredSet = new Set(registered);
  return effectiveMixLineup(includeDevFallback)
    .filter((entry) => registeredSet.has(entry.slug))
    .map((entry) => entry.slug);
}

/**
 * 総合カタログのアンカー板を決める。ラインナップ優先順で登録済みの先頭。
 *
 * アンカー板は `X-Board-Key` 既定値とトークン発行元という内部配線のためだけの
 * 存在で、板としての優劣や表示上の特別扱いは無い。先頭の板が未登録の環境では
 * 次点が昇格する。
 */
export function resolveMixAnchorSlug(
  registered: readonly string[],
  includeDevFallback: boolean = defaultIncludeDevFallback(),
): string | null {
  return filterMixLineupSlugs(registered, includeDevFallback)[0] ?? null;
}

export function findMixLineupEntry(
  slug: string,
  includeDevFallback: boolean = defaultIncludeDevFallback(),
): MixLineupEntry | undefined {
  return effectiveMixLineup(includeDevFallback).find(
    (entry) => entry.slug === slug,
  );
}

/** 区分が不明な板は成人確認を必要とする。 */
export function isAdultMixBoard(slug: string): boolean {
  return findMixLineupEntry(slug)?.isAdult ?? true;
}

import { getBoardPresentations } from "@/config/boardPresentation";
